import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom, Observable, tap} from "rxjs";
import {LoginCommand, AuthClient, PermissionType, AccessType, ResetPasswordCommand, AuthResponseDto} from './web-api-client';
import {jwtDecode} from 'jwt-decode';
import {map, switchMap} from "rxjs/operators";
import {MenuItem, navigation} from '../app-navigation';
import {UserRole} from "../shared/constants";

export interface IAccessTokenData {
    nameid: string;
    email: string;
    Permissions?: string[] | string;
    ImpersonatorId?: string | boolean;
    role: string[];
}

export const defaultPath = '/';

@Injectable()
export class AuthService {
    private router = inject(Router);
    private auth = inject(AuthClient);

    private _tokenData: IAccessTokenData | null = null;
    private authState = new BehaviorSubject<boolean>(this.hasToken());
    private accessToken: string | null = null;
    private _lastAuthenticatedPath: string = defaultPath;
    private permissionsMap = new Map<string, number>();
    private refreshInFlight: Promise<AuthResponseDto | undefined> | null = null;

    private readonly accessTypeWeights: Record<AccessType, number> = {
        [AccessType.Hidden]: 0,
        [AccessType.ViewOnly]: 1,
        [AccessType.Edit]: 2,
        [AccessType.Manage]: 3,
    };

    get isImpersonated(): boolean {
        return !!this._tokenData?.ImpersonatorId;
    }

    get isAdministrator(): boolean {
        return !!this._tokenData?.role?.includes('Administrator');
    }

    canImpersonate(targetId: string | null | undefined, targetRoles: string[] | null | undefined): boolean {
        if (!this.checkPermission(PermissionType.Impersonate, AccessType.Manage))
            return false;

        if (this.isOwnProfile(targetId))
            return false;

        const targetIsAdministrator = !!targetRoles?.includes('Administrator');
        return this.isAdministrator || !targetIsAdministrator;
    }

    isOwnProfile(userId: string | null | undefined): boolean {
        return !!userId && userId === this.userId;
    }

    canViewProfileSection(userId: string | null | undefined, own: PermissionType, others: PermissionType): boolean {
        return this.isOwnProfile(userId)
            ? this.checkPermission(own, AccessType.ViewOnly)
            : this.checkPermission(others, AccessType.ViewOnly);
    }

    canEditProfileSection(userId: string | null | undefined, own: PermissionType, others: PermissionType): boolean {
        return this.isOwnProfile(userId)
            ? this.checkPermission(own, AccessType.Edit)
            : this.checkPermission(others, AccessType.Manage);
    }

    // Agency users' View Only also reaches the employees of their agencies; the server checks the employee is one of them.
    canViewOtherEmployees(permission: PermissionType): boolean {
        return this.checkPermission(permission, AccessType.Manage)
            || (this.hasRole(UserRole.Agency) && this.checkPermission(permission, AccessType.ViewOnly));
    }

    // A null userId is the all-employees score; the server limits it to the employees the user may see.
    canViewMyScoreSection(userId: string | null | undefined, permission: PermissionType): boolean {
        if (this.isOwnProfile(userId))
            return this.checkPermission(permission, AccessType.ViewOnly);

        return this.canViewOtherEmployees(permission);
    }

    // A single role is decoded as a plain string, where includes() would match substrings ("Agency Employee").
    private hasRole(role: UserRole): boolean {
        const roles: string[] | string = this._tokenData?.role ?? [];
        return Array.isArray(roles) ? roles.includes(role) : roles === role;
    }

    private hasToken(): boolean {
        return !!this.accessToken;
    }

    isAuthenticated$(): Observable<boolean> {
        return this.authState.asObservable();
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    private setAccessToken(token: string) {
        this.accessToken = token;
        this._tokenData = jwtDecode<IAccessTokenData>(token);
        this.parsePermissions(this._tokenData.Permissions);
        this.authState.next(true);
    }

    private clearAccessToken() {
        this.accessToken = null;
        this._tokenData = null;
        this.permissionsMap.clear();
        this.authState.next(false);
    }

    private parsePermissions(permissions: string[] | string | undefined) {
        this.permissionsMap.clear();
        if (!permissions) return;

        const permsArray = Array.isArray(permissions) ? permissions : [permissions];

        for (const perm of permsArray) {
            // Expected format: "PermissionType:AccessType"
            const parts = perm.split(':');
            if (parts.length === 2) {
                const type = parts[0];
                const access = parts[1] as AccessType;

                if (access in this.accessTypeWeights) {
                    const weight = this.accessTypeWeights[access];
                    // Store the weight. logic handles hierarchy (Manage > Edit > ViewOnly)
                    this.permissionsMap.set(type, weight);
                }
            }
        }
    }

    checkPermission(permission: PermissionType, requiredAccess: AccessType): boolean {
        if (!this._tokenData)
            return false;

        if (this._tokenData.role.includes('Administrator'))
            return true;

        const userAccessLevel = this.permissionsMap.get(permission) ?? 0;
        const requiredLevel = this.accessTypeWeights[requiredAccess];
        return userAccessLevel >= requiredLevel;
    }

    isMenuItemAuthorized(item: MenuItem): boolean {
        if (!item.permission)
            return true;

        const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];

        return permissions.some(p => this.checkPermission(p, AccessType.ViewOnly));
    }

    set lastAuthenticatedPath(value: string) {
        this._lastAuthenticatedPath = value;
    }

    logIn(email: string, password: string): Observable<void> {
        return this.auth.login(new LoginCommand({email, password})).pipe(
            tap(res => {
                if (res?.accessToken) {
                    this.setAccessToken(res.accessToken);
                }
            }),
            switchMap(() => this.router.navigate([this.resolveRedirectPath()])),
            map(() => void 0)
        );
    }

    // Resolve where to land after login. A deep link captured by the auth guard is honoured;
    // otherwise fall back to the default landing page based on the user's permissions.
    private resolveRedirectPath(): string {
        if (this._lastAuthenticatedPath && this._lastAuthenticatedPath !== defaultPath) {
            return this._lastAuthenticatedPath;
        }

        return this.getLandingPath();
    }

    // My Score – Employment when the user can access it, otherwise the first page they may open.
    private getLandingPath(): string {
        if (this.checkPermission(PermissionType.MyScore, AccessType.ViewOnly)) {
            return '/my-score';
        }

        return this.findFirstAccessiblePath(navigation) ?? defaultPath;
    }

    private findFirstAccessiblePath(items: MenuItem[]): string | null {
        for (const item of items) {
            if (!this.isMenuItemAuthorized(item))
                continue;

            if (item.items?.length) {
                const childPath = this.findFirstAccessiblePath(item.items);
                if (childPath)
                    return childPath;
            } else if (item.path) {
                return item.path;
            }
        }

        return null;
    }

    changePassword(newPassword: string, recoveryCode: string) {
        return this.auth.resetPassword(new ResetPasswordCommand({
            token: recoveryCode,
            newPassword: newPassword
        }));
    }

    resetPassword(email: string): Observable<void> {
        return this.auth.sendPasswordResetLink(email);
    }

    impersonate(userId: string): Observable<void> {
        return this.auth.impersonate(userId).pipe(
            tap(res => {
                if (res?.accessToken) {
                    this.setAccessToken(res.accessToken);
                }
            }),
            map(() => void 0)
        );
    }

    undoImpersonate(): Observable<void> {
        return this.auth.undoImpersonate().pipe(
            tap(res => {
                if (res?.accessToken) {
                    this.setAccessToken(res.accessToken);
                }
            }),
            map(() => void 0)
        );
    }

    async refresh(): Promise<AuthResponseDto | undefined> {
        // Single-flight: concurrent 401s must share one refresh call so the
        // single-use refresh token is rotated exactly once. Without this, a
        // second concurrent refresh would present the already-revoked token and
        // fail, logging the user out mid-session.
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }

        this.refreshInFlight = this.performRefresh();
        try {
            return await this.refreshInFlight;
        } finally {
            this.refreshInFlight = null;
        }
    }

    private async performRefresh(): Promise<AuthResponseDto | undefined> {
        try {
            const res = await firstValueFrom(this.auth.refresh());
            if (res && res.accessToken) {
                this.setAccessToken(res.accessToken);
            }
            return res;
        } catch {
            this.clearAccessToken();
            throw new Error('Refresh failed');
        }
    }

    async logOut() {
        try {
            await firstValueFrom(this.auth.logout());
        } finally {
            this.clearAccessToken();
        }

        await this.router.navigate(['/auth/sign-in']);
    }

    get userId(): string | null {
        return this._tokenData?.nameid ?? null;
    }
}
